/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-use-before-define */
import { h, createContext } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import Storefront from '../utils/storefront';
import * as queries from '../utils/storefront/queries';
import { formatCart } from '../utils/storefront/format-resource';
import { getCookie, hasEnvelopeLineItem } from '../utils/helpers';

const Context = createContext();

const CartContext = ({ children, data }) => {
  const storefront = new Storefront();
  const [cartState, setCartState] = useState({
    id: null,
    subtotal: { amount: 0 },
    lines: [],
    gwpLinkFree: false,
  });
  const [cartLoading, setCartLoading] = useState(false);

  const openCartEvent = new CustomEvent('cart:open');

  /**
   * Gets a cart instance from a cart ID
   * @param {string} id cart id
   * @returns formatted cart object
   */
  const getCart = async (id) => {
    if (!id) return null;
    setCartLoading(true);
    const response = await storefront.request({
      query: queries.cartGet,
      variables: {
        id,
      },
    });
    setCartLoading(false);

    if (response?.data?.cart) return formatCart(response.data.cart);
    // Remove old ID from localStorage
    localStorage.removeItem('TMW.cartToken');
    return null;
  };

  /**
   * Creates a new cart instance
   * @returns formatted cart object
   */
  const createCart = async () => {
    const response = await storefront.request({
      query: queries.cartCreate,
      variables: {
        input: {},
      },
    });

    if (response?.data?.cartCreate?.cart) {
      const { cart } = response.data.cartCreate;
      // Store ID in localStorage for future use
      localStorage.setItem('TMW.cartToken', cart.id);
      return formatCart(cart);
    }
    return false;
  };

  useEffect(() => {
    async function fetchData() {
      let initialCart = await getCart(localStorage.getItem('TMW.cartToken'));
      if (initialCart?.id)
        localStorage.setItem('TMW.cartToken', initialCart.id);
      if (initialCart) return setCartState(initialCart);
      initialCart = await createCart();
      setCartState(initialCart);
      return false;
    }
    fetchData();
  }, []);

  /**
   * Method for handling all cart requests (add/remove/update)
   * @param {array} lines
   * @param {array} lineIds
   * @param {array} attributes
   * @param {string} query
   * @returns cart object (stored in state)
   */
  const updateCartLines = async ({
    lines,
    lineIds,
    attributes,
    query,
    isBackgroundJob = false,
    withoutLoading = false,
  }) => {
    if (!withoutLoading) {
      setCartLoading(true);
    }
    const response = await storefront.request({
      query: queries[query],
      variables: {
        cartId: cartState.id,
        ...(lines && { lines }),
        ...(lineIds && { lineIds }),
        ...(attributes && { attributes }),
      },
    });

    if (response.errors) console.error(response.errors);
    const updatedCart = formatCart(response.data[query].cart);
    const zeroQuantityItems = updatedCart.lines.filter(
      (line) => line.quantity === 0,
    );

    // Remove zero quantity items
    if (zeroQuantityItems.length) {
      cart.remove(
        zeroQuantityItems.map((line) => line.id),
        true,
      );
    }

    // remove envelope product
    const findEnvelopeItem = (cartLines, envelopeType) =>
      (cartLines || []).find((item) => {
        const attributes = item.attributes || [];
        return (
          attributes.some(
            (attr) => attr.key === 'envelope' && attr.value === 'true',
          ) &&
          attributes.some(
            (attr) =>
              attr.key === 'envelope_type' && attr.value === envelopeType,
          )
        );
      });

    const removeEnvelopeItemIfNotInCart = (
      cartLines,
      productHandle,
      envelopeType,
    ) => {
      if (
        productHandle &&
        !(cartLines || []).some(
          (item) => item.merchandise.product.handle === productHandle,
        ) &&
        hasEnvelopeLineItem(cartLines, envelopeType)
      ) {
        const envelopeItem = findEnvelopeItem(cartLines, envelopeType);
        if (envelopeItem) {
          cart.remove(envelopeItem.id, false);
        }
      }
    };

    removeEnvelopeItemIfNotInCart(
      updatedCart?.lines,
      data?.service_product_warranty_handle,
      'warranty',
    );
    removeEnvelopeItemIfNotInCart(
      updatedCart?.lines,
      data?.service_product_lifesharp_handle,
      'lifesharp',
    );

    // Update cart
    if (!isBackgroundJob) {
      setCartState(updatedCart);
      document.dispatchEvent(openCartEvent);
    }

    // still want to update cart but not open it
    // if we're setting attributes
    if (attributes) {
      setCartState(updatedCart);
    }
    if (!withoutLoading) {
      setCartLoading(false);
    }
  };

  /**
   * Formats line items for storefront API
   * @param {array|object} payload of line items to add or update
   * @returns payload formatted for the storefront API
   */
  const formatCartUpdatePayload = (payload, updateType) => {
    if (!payload || !updateType) return [];
    const payloadArray = Array.isArray(payload) ? payload : [payload];
    if (updateType === 'add') {
      return payloadArray.map((line) => {
        const merchandiseId = line.id;
        delete line.id;
        return {
          ...line,
          merchandiseId,
        };
      });
    }

    if (updateType === 'updateAttributes') {
      return [...cart.state.attributes, ...payloadArray];
    }

    return payloadArray;
  };

  /**
   * Refreshes the cart state by fetching the latest data from the server.
   *
   * This method retrieves the current cart ID from local storage, fetches the
   * updated cart information using the `getCart` method, and updates the
   * cart state (`cartState`).
   *
   * @returns {Promise<void>} Resolves when the cart state is refreshed.
   */
  const refreshCart = async () => {
    const cartId = localStorage.getItem('TMW.cartToken');
    if (!cartId) {
      console.warn('No cart ID found. Cannot refresh cart.');
      return;
    }

    try {
      setCartLoading(true);
      const updatedCart = await getCart(cartId);
      if (updatedCart) {
        setCartState(updatedCart);
      } else {
        console.warn('Failed to refresh cart. Cart may not exist.');
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    } finally {
      setCartLoading(false);
    }
  };

  /**
   * Wrapper for cart API to make interactions more simple
   */
  const cart = {
    add: (payload, isBackgroundJob) =>
      updateCartLines({
        lines: formatCartUpdatePayload(payload, 'add'),
        query: 'cartLinesAdd',
        isBackgroundJob,
      }),
    remove: (payload, isBackgroundJob) =>
      updateCartLines({
        lineIds: formatCartUpdatePayload(payload, 'remove'),
        query: 'cartLinesRemove',
        isBackgroundJob,
      }),
    update: (payload, isBackgroundJob, withoutLoading) =>
      updateCartLines({
        lines: formatCartUpdatePayload(payload, 'update'),
        query: 'cartLinesUpdate',
        isBackgroundJob,
        withoutLoading,
      }),
    updateAttributes: (payload) =>
      updateCartLines({
        attributes: formatCartUpdatePayload(payload, 'updateAttributes'),
        query: 'cartAttributesUpdate',
        isBackgroundJob: true,
      }),
    state: cartState,
    refresh: refreshCart,
  };

  window.GlobalCartContext = cart;

  const handleExternalAddToCart = (e) => {
    cart.add(e.detail.payload);
  };

  const handleExternalUpdateAttributes = (e) => {
    cart.updateAttributes(e.detail.payload);
  };

  const checkAndAddGWPLink = () => {
    const gwpLinkInCart = cartState.lines.find((line) =>
      line.attributes.find((attribute) => attribute.key === '_gwp_link'),
    );
    if (!gwpLinkInCart) {
      cart.add({
        id: `gid://shopify/ProductVariant/${data.gwpLinkVariant}`,
        quantity: 1,
        attributes: [
          {
            key: '_gwp_link',
            value: 'true',
          },
          {
            key: '_gwp_link_threshold',
            value: data.gwpLinkThreshold?.toString(),
          },
          {
            key: '_93067a65dac5',
            value: `${(data.gwpThreshold + 18417) * 5}`,
          },
        ],
      });
    }

    const currentUrl = window.location.href;
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('p');
    const newUrl = `${currentUrl.split('?')[0]}?${searchParams.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  useEffect(() => {
    if (!cartState.id) return;
    // Setup cart events for components outside of this context
    document.addEventListener('cart:add', handleExternalAddToCart);
    document.addEventListener(
      'cart:updateAttributes',
      handleExternalUpdateAttributes,
    );

    const gwpInCart = cartState.lines.find((line) =>
      line.attributes.find((attribute) => attribute.key === '_is_gwp'),
    );
    let gwpThresholdReached = false;
    let gwpProductsReached = false;
    let isRelevantGwp = true;
    const gwpRemovedByUser = getCookie(`disabled_gwp`);

    if (gwpInCart) {
      isRelevantGwp =
        gwpInCart?.merchandise?.id ===
        `gid://shopify/ProductVariant/${data.gwpProduct.id}`;
    }

    if (data.gwpEnabled && data.gwpProduct.id && isRelevantGwp) {
      if (data.gwpThreshold > 0) {
        if (data.gwpThreshold <= parseFloat(cartState.subtotal.amount)) {
          gwpThresholdReached = true;
        }
      }
      if (data.gwpProducts) {
        gwpProductsReached = !!cartState.lines.find((line) => {
          const productId = parseInt(
            line.merchandise.product.id.replace('gid://shopify/Product/', ''),
            10,
          );
          return data.gwpProducts.includes(productId);
        });
      }

      const gwpLinkInCart = cartState.lines.find((line) =>
        line.attributes.find((attribute) => attribute.key === '_gwp_link'),
      );
      if (gwpProductsReached || gwpThresholdReached) {
        if (!gwpInCart && !gwpRemovedByUser && !gwpLinkInCart) {
          cart.add({
            id: `gid://shopify/ProductVariant/${data.gwpProduct.id}`,
            quantity: 1,
            attributes: [
              {
                key: '_is_gwp',
                value: 'true',
              },
              {
                key: '_is_simple',
                value: 'true',
              },
              {
                key: '_93067a65dac5',
                value: `${(data.gwpThreshold + 18417) * 5}`,
              },
            ],
          });
        }
      } else if (gwpInCart) {
        cart.remove(
          cartState.lines.find((line) =>
            line.attributes.find((attribute) => attribute.key === '_is_gwp'),
          ).id,
          false,
        );
      }
    } else if (gwpInCart) {
      cart.remove(
        cartState.lines.find((line) =>
          line.attributes.find((attribute) => attribute.key === '_is_gwp'),
        ).id,
        false,
      );
    }

    const cartCountEl = document.querySelector('[data-cart-count]');
    if (cartCountEl) {
      cartCountEl.textContent =
        cartState?.lines
          // eslint-disable-next-line no-return-assign
          ?.reduce((total, line) => {
            const isComponent = line.attributes.find(
              (attr) => attr.key === '_is_component' && attr.value === 'true',
            );

            return isComponent ? total : total + line.quantity;
          }, 0) ?? 0;
    }

    const gwpParam = new URLSearchParams(window.location.search).get('p');
    if (gwpParam === 'gwp-link') {
      checkAndAddGWPLink();
      document.dispatchEvent(openCartEvent);
    }
    // Cleanup
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('cart:add', handleExternalAddToCart);
      document.removeEventListener(
        'cart:updateAttributes',
        handleExternalUpdateAttributes,
      );
    };
  }, [cartState]);

  // Use effect for the integritiy of custom items
  useEffect(() => {
    const customizationItems = cartState?.lines?.filter((line) =>
      line.attributes.find(
        (attribute) =>
          attribute.key === '_is_component' ||
          attribute.key === '_personalization' ||
          attribute.key === '_is_main',
      ),
    );

    // group the component items based on matching _threekit_id attribute values
    const groupedComponentItems = Object.values(
      customizationItems?.reduce((acc, item) => {
        const threekitId = item.attributes.find(
          (attribute) => attribute.key === '_threekit_id',
        )?.value;

        if (!threekitId) return acc;

        if (!acc[threekitId]) {
          acc[threekitId] = [];
        }

        acc[threekitId].push(item);

        return acc;
      }, {}),
    );

    if (!groupedComponentItems.length) return;

    // ensure that the component items have a parent item.
    // If not, remove from cart.
    groupedComponentItems.forEach((group) => {
      const hasMainItem = group.find((item) =>
        item.attributes.find(
          (attribute) =>
            attribute.key === '_is_main' ||
            attribute.key === '_personalization',
        ),
      );
      if (!hasMainItem) {
        cart.remove(group.map((item) => item.id));
      }
    });
  }, [cartState]);

  return (
    <Context.Provider value={{ cart, data, cartLoading }}>
      {children}
    </Context.Provider>
  );
};

// eslint-disable-next-line react/display-name
export const withCartContext = (Rendered) => (props) =>
  (
    <Context.Consumer>
      {(value) => <Rendered {...props} {...value} />}
    </Context.Consumer>
  );

export default CartContext;
